import React, { useEffect, useState } from 'react';

//application type definitions
import { GroupUnion } from '../../models/GroupUnion';

//styles
import styles from './SizingTools.module.scss';
import './switch.scss';

import { Accordion, AccordionDetails, AccordionSummary, Button, FormControl, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import {
  GetManifoldAndValvesSizingProgressResponse,
  GetSystemResponse,
  TemperatureUnits,
  ValveSequencingApplication,
  SizeManifoldAndValvesInputs,
  SizeValveOnManifoldRequest,
  UnitGroup,
  UnitDefinition,
  ProductCacheStatus,
  VolumetricFlowRateUnits,
  ComparisonViewModel,
  ProductSeriesFilterResult,
  Part,
  VirtualGroup,
  Metadata,
  SizeManifoldAndValvesRequest,
  ComparisonProduct,
  ComparisonAttribute,
} from '../../models/Api';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import AirFlowCalculator from './AirFlowCalculator';
import UnitService from '../../services/UnitService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SizingToolHeader from './SizingToolHeader';
import { convertDecimalStringToNumber, convertUnitTo, toDecimals, getNumberValidator, isMetricUnitsInUse } from '../../utils/Utils';
import UnitPicker from './UnitPicker';
import SizingToolService, { compareCleanupEventType, tabClosingEventType } from '../../services/SizingToolService';
import ResultsPanel from './ResultsPanel';
import CompareTool from '../CompareTool/CompareTool';
import SystemService from '../../services/SystemService';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';

export type FormValues = {
  electricalConnectionToManifold: string;
  electricalConnectionCategory: string;
  electricalConnectionType: string;
  portType: string;
  minTemperature: string;
  maxTemperature: string;
};

export const LightTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(0, 0, 0, 0.1)',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}));

type ProductSeriesResults = ProductSeriesFilterResult[];
type ComparisonViewModelCompound = {
  allProducts: ComparisonViewModel;
  selectedProducts: ComparisonViewModel;
};
type OptionInfo = {
  displayText: string;
  value: string;
  disabled: boolean;
  selected: boolean;
};

export default function PdnManifoldSizingTool(props: { part: Part; manifoldGroup: GroupUnion; segmentGroup: GroupUnion; system: GetSystemResponse }) {
  const { t } = useTranslation();
  const filteredAirflowUnits = UnitService.getFilteredUnits(UnitGroup.VolumetricFlowRate, 2);
  const divisionId = props.system.divisionId as number;
  const [valveSequencingApplications, setValveSequencingApplications] = useState([] as ValveSequencingApplication[]);
  const [showCompare, setShowCompare] = useState<boolean>(false);
  const [comparisonViewModel, setComparisonViewModel] = useState<ComparisonViewModelCompound>({
    allProducts: {} as ComparisonViewModel,
    selectedProducts: {} as ComparisonViewModel,
  });
  const [sizingProgress, setSizingProgress] = useState<GetManifoldAndValvesSizingProgressResponse>({});
  const [initialInputs, setInitialInputs] = useState<SizeManifoldAndValvesInputs | undefined>(undefined);
  const [showAirFlowCalculator, setShowAirFlowCalculator] = useState<boolean>(false);
  const [isMetric, setIsMetric] = useState<boolean>(isMetricUnitsInUse());
  const [stationsAndMaxAirflowErrorAlert, setStationsAndMaxAirflowErrorAlert] = useState<boolean>(false);
  const [stationsAndMaxAirflowTouched, setStationsAndMaxAirflowTouched] = useState<{
    maxAirflow: boolean;
    stations: boolean;
  }>({ maxAirflow: false, stations: false });
  const [previousSeriesResults, setPreviousSeriesResults] = useState<ProductSeriesResults>([]);
  const [previousProductsToCompare, setPreviousProductsToCompare] = useState<ProductSeriesResults>([]);

  const defaultTemperatureUnit = UnitService.getDefaultUnitForSystemOfMeasurement(isMetric, UnitGroup.Temperature, TemperatureUnits);
  const defaultVolumetricUnit = UnitService.getDefaultUnitForSystemOfMeasurement(isMetric, UnitGroup.VolumetricFlowRate, VolumetricFlowRateUnits);

  const convertFromCelciusToDefaultTemperatureUnit = (value: number) => {
    const fromUnitDefinition = UnitService.getUnitDefinitionByGroupAndName(UnitGroup.Temperature, TemperatureUnits.Celcius) as UnitDefinition;
    const toUnitDefinition = UnitService.getUnitDefinitionByGroupAndName(UnitGroup.Temperature, defaultTemperatureUnit) as UnitDefinition;
    return convertUnitTo(UnitGroup.Temperature, fromUnitDefinition.unit as number, value, toUnitDefinition.unit as number);
  };
  const getDefaultMinTemperatureValue = () => {
    const defaultValue = 10;
    if (defaultTemperatureUnit === TemperatureUnits.Celcius) return defaultValue;
    return convertFromCelciusToDefaultTemperatureUnit(defaultValue);
  };

  const getDefaultMaxTemperatureValue = () => {
    const defaultValue = 40;
    if (defaultTemperatureUnit === TemperatureUnits.Celcius) return defaultValue;
    return convertFromCelciusToDefaultTemperatureUnit(defaultValue);
  };

  const closeCompare = () => {
    setShowCompare(false);
  };

  const handleCompare = (selectedSeries: ProductSeriesFilterResult[]) => {
    // Create a deep copy of all products because relative pricing calculation needs to overwrite original pricing
    const clonedComparisonViewModel = JSON.parse(JSON.stringify(comparisonViewModel.allProducts)) as ComparisonViewModel;
    const filteredCompareViewModel = { ...clonedComparisonViewModel };
    filteredCompareViewModel.products = filteredCompareViewModel.products?.filter((product) =>
      selectedSeries.some((productSeries) => productSeries.seriesId === product.seriesId),
    );

    // PDN likes their relative pricing to be based on the selected series going into Compare
    const TotalPriceHeaderKey = 'Total Price';
    const priceAttributeIndex = filteredCompareViewModel.attributeHeaders?.findIndex(
      (attributeHeader) => attributeHeader.originalKeyName === TotalPriceHeaderKey,
    );
    if (priceAttributeIndex === undefined || priceAttributeIndex < 0) {
      throw Error(`Cannot find attribute header "${TotalPriceHeaderKey}" required to calculate relative pricing`);
    }
    const pricesMap = new Map<number, number>();
    const allProducts = clonedComparisonViewModel.products as ComparisonProduct[];
    for (const product of allProducts) {
      const attributes = product.attributes as ComparisonAttribute[];
      const absolutePrice = attributes[priceAttributeIndex].valueNumber ?? 0;
      if (absolutePrice > 0 && selectedSeries.some((productSeries) => productSeries.seriesId === product.seriesId)) {
        pricesMap.set(product.index as number, absolutePrice);
      }
    }
    const selectedProducts = filteredCompareViewModel.products as ComparisonProduct[];
    const minPrice = pricesMap.size > 0 ? Math.min(...Array.from(pricesMap.values())) : 0;
    if (minPrice > 0) {
      for (const product of selectedProducts) {
        const attributes = product.attributes as ComparisonAttribute[];
        const absolutePrice = pricesMap.get(product.index as number) ?? 0;
        attributes[priceAttributeIndex].valueNumber = absolutePrice / minPrice;
      }
    }

    setComparisonViewModel({
      allProducts: comparisonViewModel.allProducts,
      selectedProducts: filteredCompareViewModel,
    });
    setShowCompare(true);
  };

  const guideMap = new Map();
  guideMap.set('en', 'PDN Manifold Sizing Guide.pdf');

  //Operator Voltage
  const valveOperatorVoltageOptionsMap = new Map();
  valveOperatorVoltageOptionsMap.set(`${t('12VDC')}`, '12VDC');
  valveOperatorVoltageOptionsMap.set(`${t('24VDC')}`, '24VDC');
  valveOperatorVoltageOptionsMap.set(`${t('24VAC')}`, '24VAC');

  valveOperatorVoltageOptionsMap.set(`${t('120VAC')}`, '120VAC');
  valveOperatorVoltageOptionsMap.set(`${t('240VAC')}`, '240VAC');
  valveOperatorVoltageOptionsMap.set(`${t('RemotePilot')}`, 'N/A');

  //Electrical Connection
  const valveElectricalConnectionOptionsMap = new Map();
  valveElectricalConnectionOptionsMap.set(`${t('Plug-in')}`, 'Plug-in');
  valveElectricalConnectionOptionsMap.set(`${t('Non-Plug-in')}`, 'Non Plug-in');

  //Connection Category
  const manifoldElectricalConnectionCategoryOptionsMap = new Map();
  manifoldElectricalConnectionCategoryOptionsMap.set(`${t('Industrial-Network-Fieldbus')}`, 'Industrial Network-Fieldbus');
  manifoldElectricalConnectionCategoryOptionsMap.set(`${t('EndPlateCollectiveWire')}`, 'End Plate Collective Wire');

  //Connection Type
  const manifoldElectricalConnectionTypeOptionsMap = new Map();
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('IO-Link')}`, 'IO-Link');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('Devicenet')}`, 'Devicenet');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('Ethernet')}`, 'Ethernet');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('Profibus')}`, 'Profibus');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('PowerLink')}`, 'PowerLink');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('EtherCAT')}`, 'EtherCat');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('Canopen')}`, 'Canopen');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('Profinet')}`, 'Profinet');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('ModbusTCP')}`, 'ModbusTCP');

  manifoldElectricalConnectionTypeOptionsMap.set(`${t('25-pin-D-sub')}`, '25 pin D-sub');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('12-pin-M23')}`, '12 pin M23');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('19-pin-M23')}`, '19 pin M23');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('19-pin-Brad-Harrison')}`, '19 pin Brad Harrison');
  manifoldElectricalConnectionTypeOptionsMap.set(`${t('Conduit')}`, 'Conduit');

  const industrialFieldbusTypes = Array.from(manifoldElectricalConnectionTypeOptionsMap.keys()).slice(0, 9);

  const endPlateCollectiveWireTypes = Array.from(manifoldElectricalConnectionTypeOptionsMap.keys()).slice(9, 15);

  //port type
  const portTypeOptionsMap = new Map();
  portTypeOptionsMap.set(`${t('NPT/Inch-OD-Tube')}`, 'NPT / Inch OD Tube');
  portTypeOptionsMap.set(`${t('BSPP/Metric-OD-Tube')}`, 'BSPP / Metric OD Tube');

  const valveFunctionOptions = ['3/2', '5/2', '5/3 CE', '5/3 PC', '5/3 APB', '4/2', '4/3 CE', '4/3 PC', '4/3 APB'];

  const valveOperatorTypeOptionsMap = new Map();
  valveOperatorTypeOptionsMap.set(`${t('SingleSolenoid')}`, 'Single Solenoid');
  valveOperatorTypeOptionsMap.set(`${t('DoubleSolenoid')}`, 'Double Solenoid');
  valveOperatorTypeOptionsMap.set(`${t('SingleRemotePilot')}`, 'Single Remote Pilot');
  valveOperatorTypeOptionsMap.set(`${t('DoubleRemotePilot')}`, 'Double Remote Pilot');

  const isNonPlugInSelected = sizingProgress?.savedSizingRequest?.electricalConnectionToManifold === t('Non-Plug-in');
  const isPlugInSelected = sizingProgress?.savedSizingRequest?.electricalConnectionToManifold === t('Plug-in');

  const onlyEndPlateCollectiveWireIsValid =
    isPlugInSelected &&
    [
      valveOperatorVoltageOptionsMap.get(`${t('12VDC')}`),
      valveOperatorVoltageOptionsMap.get(`${t('24VAC')}`),
      valveOperatorVoltageOptionsMap.get(`${t('120VAC')}`),
    ].some((voltageOption) => voltageOption === sizingProgress.savedSizingRequest?.valveOperatorVoltage);

  const localizableNumber = yup.number().transform((value, originalValue) => {
    return getNumberValidator(originalValue, props.system.locale as string);
  });
  const thisFieldIsRequiredMessage = t('ThisFieldIsRequired');

  yup.setLocale({
    mixed: { required: thisFieldIsRequiredMessage, notType: t('ValueIsNotANumber') },
    number: { positive: t('ValueMustBePositive') },
  });

  const basicSizingTabValidationSchema = yup.object().shape({
    electricalConnectionToManifold: yup.string(),
    electricalConnectionCategory: yup.string().when(['electricalConnectionToManifold'], {
      is: () => sizingProgress?.savedSizingRequest?.electricalConnectionToManifold === t('Plug-in'),
      then: yup.string().required(),
    }),
    electricalConnectionType: yup.string().when(['electricalConnectionToManifold'], {
      is: () => sizingProgress?.savedSizingRequest?.electricalConnectionToManifold === t('Plug-in'),
      then: yup.string().required(),
    }),
    portType: yup.string().required(),
    minTemperature: localizableNumber
      .required()
      .test('lessThanTest', t('ValueShouldBeLessThan', { fieldName: t('ValveMaximumTemperature') }), function (value: number | undefined) {
        const min = value ?? NaN;
        if (!this.parent.maxTemperature || isNaN(this.parent.maxTemperature)) return true;
        return min <= this.parent.maxTemperature;
      }),

    maxTemperature: localizableNumber
      .required()
      .test('greaterThanTest', t('ValueShouldBeGreaterThan', { fieldName: t('ValveMinimumTemperature') }), function (value: number | undefined) {
        const max = value ?? NaN;
        if (!this.parent.minTemperature || isNaN(this.parent.minTemperature)) return true;
        return max >= this.parent.minTemperature;
      }),
  });

  const validateStationsAndMaxAirflow = (shouldTouchMaxAirflow?: boolean) => {
    setStationsAndMaxAirflowErrorAlert(true);
    if (shouldTouchMaxAirflow)
      setStationsAndMaxAirflowTouched({
        maxAirflow: true,
        stations: stationsAndMaxAirflowTouched.stations,
      });
    const areStationsValid = sizingProgress?.savedSizingRequest?.valveInputs?.every((valveInput) => valveInput.valveFunction && valveInput.operatorType);
    const isMaxAirflowValid = typeof sizingProgress.savedSizingRequest?.maxAirflow === 'number' && sizingProgress.savedSizingRequest.maxAirflow > 0;
    return areStationsValid && isMaxAirflowValid;
  };

  const isMaxAirflowMissing =
    !sizingProgress.savedSizingRequest?.maxAirflow &&
    sizingProgress.savedSizingRequest?.maxAirflow !== 0 &&
    stationsAndMaxAirflowTouched.maxAirflow &&
    stationsAndMaxAirflowErrorAlert;

  const invalidMaxAirflow = typeof sizingProgress.savedSizingRequest?.maxAirflow === 'number' && sizingProgress.savedSizingRequest.maxAirflow <= 0;

  const isAnyValveOptionMissing = stationsAndMaxAirflowTouched.stations && stationsAndMaxAirflowErrorAlert;

  const manifoldPartCategory = props.system.partCategories?.find((category) => category.name === 'PneumaticManifoldMasterPart');

  const saveManifoldAndValvesInputs = () => {
    SizingToolService.createSizingManifoldAndValvesProgress(props.part.workflowId as string, {
      workflowId: props.part.workflowId,
      inputs: sizingProgress?.savedSizingRequest,
    });
  };

  const updateTemperatureValues = (values: FormValues) => {
    const sizingProgressUpdated = { ...sizingProgress };
    if (sizingProgressUpdated.savedSizingRequest) {
      sizingProgressUpdated.savedSizingRequest.minTemperature = convertDecimalStringToNumber(values.minTemperature, props.system.locale);
      sizingProgressUpdated.savedSizingRequest.maxTemperature = convertDecimalStringToNumber(values?.maxTemperature, props.system.locale);
    }
    setSizingProgress(sizingProgressUpdated);
  };

  const updateManifoldElectricalConnectionCategoryAndType = () => {
    const sizingProgressUpdated = { ...sizingProgress };
    if (isNonPlugInSelected) {
      if (sizingProgressUpdated.savedSizingRequest) {
        sizingProgressUpdated.savedSizingRequest.electricalConnectionCategory = 'None';
        sizingProgressUpdated.savedSizingRequest.electricalConnectionType = 'No Electrical';
      }
    }
    setSizingProgress(sizingProgressUpdated);
  };

  //update sizing progress values before submit
  const updateSizingInputsFromValues = (values: FormValues) => {
    //handle decimal strings
    updateTemperatureValues(values);
    //update radio option values
    updateManifoldElectricalConnectionCategoryAndType();
  };

  const renderInvalidFieldsErrorToast = () => {
    toast.error(t('PleaseUpdateItemsHighlightedInRed'));
  };

  const saveProgress = (values: FormValues) => {
    if (!validateStationsAndMaxAirflow()) {
      renderInvalidFieldsErrorToast();
      return;
    }
    setStationsAndMaxAirflowTouched({ maxAirflow: false, stations: false });
    toast.success(t('FormValuesSaved'));
    updateSizingInputsFromValues(values);
    saveManifoldAndValvesInputs();
  };

  const {
    handleChange,
    handleBlur,
    handleSubmit: handleSubmitToSaveProgress,
    resetForm,
    errors,
    touched,
    values,
    setFieldValue,
    setFieldTouched,
    setValues,
  } = useFormik({
    initialValues: {
      electricalConnectionToManifold: '',
      electricalConnectionCategory: '',
      electricalConnectionType: '',
      portType: '',
      minTemperature: '',
      maxTemperature: '',
    },
    initialTouched: {
      electricalConnectionCategory: true,
      electricalConnectionType: true,
      portType: true,
    },
    onSubmit: saveProgress,
    validationSchema: basicSizingTabValidationSchema,
  });

  const isElectricalConnectionTypeValid = () => {
    // There is something wrong with the way we clear the electrical connection type (when flipping the connection category)
    // For example, select "End Plate Collective Wire", then select "25 pin D-sub", then select "Industrial Network-Fieldbus"
    // Note how "25 pin D-sub" is deselected and disabled, but the electrical connection type (as a field) does not show
    // a validation error (it should show the "This field is required" message)
    // The backing Formik value is indeed the empty string, the electricalConnectionType in the backing savedSizingRequest object
    // is undefined and the field is touched. The field in the yup schema is marked as required.
    const isPlugin = sizingProgress?.savedSizingRequest?.electricalConnectionToManifold === t('Plug-in');
    const isCategorySelected = isPlugin ? !!sizingProgress?.savedSizingRequest?.electricalConnectionCategory : false;
    return isCategorySelected ? !!sizingProgress?.savedSizingRequest?.electricalConnectionType : true;
  };

  const validateForm = (shouldSetTouched?: boolean) => {
    if (shouldSetTouched) setStationsAndMaxAirflowTouched({ maxAirflow: true, stations: true });
    const formikHasNoErrors = Object.values(errors).length === 0;
    return formikHasNoErrors && isElectricalConnectionTypeValid() && validateStationsAndMaxAirflow();
  };

  const scrollErrorIntoView = () => {
    // Use isMaxAirflowMissing as proxy to use it as "initial scroll upon open"
    if (isMaxAirflowMissing) {
      document.getElementById('sizingToolContainer')?.scrollIntoView();
      return;
    }

    //check for max airflow
    if (invalidMaxAirflow) {
      document.getElementById('valveSequenceDiv')?.scrollIntoView();
      return;
    }

    //check for formik values
    switch (true) {
      case !!errors.electricalConnectionToManifold:
        document.getElementById('connectionToManifoldDiv')?.scrollIntoView();
        return;
      case !!errors.electricalConnectionCategory:
        document.getElementById('connectionCategoryDiv')?.scrollIntoView();
        return;
      case !!errors.electricalConnectionType:
        document.getElementById('connectionTypeDiv')?.scrollIntoView();
        return;
      case !!errors.portType:
        document.getElementById('portTypeDiv')?.scrollIntoView();
        return;
      case !!errors.minTemperature || !!errors.maxTemperature:
        document.getElementById('valveTemperatureDiv')?.scrollIntoView();
        return;
    }

    //check for valve options
    if (isAnyValveOptionMissing) {
      document.getElementsByClassName('valveOptionsDiv')[0]?.scrollIntoView();
    }
  };

  useEffect(() => {
    if (stationsAndMaxAirflowTouched.stations && stationsAndMaxAirflowErrorAlert) {
      scrollErrorIntoView();
    }
  }, [stationsAndMaxAirflowTouched.stations, stationsAndMaxAirflowErrorAlert]);

  const getAirflowInCommonUnit = (applicationGroupId: number) => {
    const flowMetadataDefinition = props.system.partMetadataDefinitions?.find((metadataDefinition) => metadataDefinition.key === 'ValveFlow');
    const virtualGroup = props.system.virtualGroups?.find((_virtualGroup) => _virtualGroup.baseGroupId === applicationGroupId) as VirtualGroup;
    const valvePart = props.system.parts?.find((part) => part.id === virtualGroup.intersectedPartId) as Part;
    const valveMetadata = valvePart.metadataInstances?.find((metadata) => metadata.definitionId === flowMetadataDefinition?.id) as Metadata;
    const selectedUnitDefinition = filteredAirflowUnits.find(
      (unitDefinition) => unitDefinition.unitName === sizingProgress?.savedSizingRequest?.maxAirflowUnit?.toString(),
    );
    const airFlow = convertUnitTo(
      flowMetadataDefinition?.unitGroup as string,
      valveMetadata?.selectedUnit as number,
      parseFloat(valveMetadata?.value as string),
      selectedUnitDefinition?.unit as number,
    );

    return airFlow;
  };

  const getValveCv = (applicationGroupId: number) => {
    const cvMetadataDefinition = props.system.partMetadataDefinitions?.find((metadataDefinition) => metadataDefinition.key === 'ValveCv');
    const virtualGroup = props.system.virtualGroups?.find((_virtualGroup) => _virtualGroup.baseGroupId === applicationGroupId) as VirtualGroup;
    const valvePart = props.system.parts?.find((part) => part.id === virtualGroup.intersectedPartId) as Part;
    const valveCv = parseFloat(valvePart.metadataInstances?.find((metadata) => metadata.definitionId === cvMetadataDefinition?.id)?.value as string);
    return valveCv;
  };

  const getApplicationsArray = () => {
    const newApplicationsArray: ValveSequencingApplication[] = [];
    const getValveOrder = (valveId: string) => {
      const valve = props.system.parts?.find((part) => part.id === valveId);
      return valve?.order as number;
    };
    const sortedVirtualGroups = props.system.virtualGroups?.sort(
      (a, b) => getValveOrder(a.intersectedPartId as string) - getValveOrder(b.intersectedPartId as string),
    );
    sortedVirtualGroups?.forEach((virtualGroup) => {
      const applicationGroup = props.system.groups?.find(
        (group) => group.id === virtualGroup.baseGroupId && virtualGroup.intersectedGroupId === props.segmentGroup.instance.id,
      );
      newApplicationsArray.push({
        id: applicationGroup?.id,
        name: applicationGroup?.name,
        airflow: getAirflowInCommonUnit(applicationGroup?.id as number),
        cv: getValveCv(applicationGroup?.id as number),
        enabled: false,
      });
    });
    return newApplicationsArray;
  };

  const getInitialValveInputs = () => {
    const initialValveInputs: SizeValveOnManifoldRequest[] = [];
    props.system.virtualGroups?.map((virtualGroup) => {
      initialValveInputs.push({
        partId: virtualGroup.intersectedPartId,
        valveFunction: undefined,
        operatorType: undefined,
        useRegulator: false,
      });
    });
    return initialValveInputs;
  };

  const getDefaultSizingRequest = () => {
    const emptySizingRequestObject: SizeManifoldAndValvesInputs = {
      valveOperatorVoltage: valveOperatorVoltageOptionsMap.get(`${t('RemotePilot')}`),
      electricalConnectionToManifold: valveElectricalConnectionOptionsMap.get(`${t('Non-Plug-in')}`),
      electricalConnectionCategory: undefined,
      electricalConnectionType: undefined,
      portType: undefined,
      minTemperature: getDefaultMinTemperatureValue(),
      minTemperatureUnit: defaultTemperatureUnit,
      maxTemperature: getDefaultMaxTemperatureValue(),
      maxTemperatureUnit: defaultTemperatureUnit,
      maxAirflow: undefined,
      maxAirflowUnit: defaultVolumetricUnit,
      isFlowCalculated: false,
      valveSequencingCycles: [],
      valveInputs: getInitialValveInputs(),
    };
    return emptySizingRequestObject;
  };

  const updateProgressDetailResponse = async () => {
    const sizingDetails = await SizingToolService.getSizingManifoldAndValvesProgressDetail(props.part.workflowId as string);
    setInitialInputs(sizingDetails.savedSizingRequest);

    if (sizingDetails?.savedSizingRequest) {
      setSizingProgress(sizingDetails);
      //update formik values
      setValues({
        electricalConnectionToManifold: sizingDetails.savedSizingRequest.electricalConnectionToManifold as string,
        electricalConnectionCategory: sizingDetails.savedSizingRequest.electricalConnectionToManifold as string,
        electricalConnectionType: sizingDetails.savedSizingRequest.electricalConnectionType as string,
        portType: sizingDetails.savedSizingRequest.portType as string,
        minTemperature: sizingDetails.savedSizingRequest.minTemperature?.toString() as string,
        maxTemperature: sizingDetails.savedSizingRequest.maxTemperature?.toString() as string,
      });
    } else {
      setSizingProgress({
        productCacheStatus: sizingDetails?.productCacheStatus,
        savedSizingRequest: getDefaultSizingRequest(),
        results: sizingDetails?.results,
      });
      //update formik values
      setValues({
        electricalConnectionToManifold: valveElectricalConnectionOptionsMap.get(`${t('Non-Plug-in')}`),
        electricalConnectionCategory: '',
        electricalConnectionType: '',
        portType: '',
        minTemperature: getDefaultMinTemperatureValue().toString(),
        maxTemperature: getDefaultMaxTemperatureValue().toString(),
      });
    }

    if (sizingDetails.results?.length) {
      setPreviousSeriesResults(sizingDetails.results);
    }
  };

  const resetSizingRequest = async () => {
    setSizingProgress({
      productCacheStatus: sizingProgress?.productCacheStatus,
      savedSizingRequest: initialInputs ? initialInputs : getDefaultSizingRequest(),
      results: sizingProgress?.results,
    });
    resetForm();
    const resetValues = { ...values };
    // Use setValues function to let Formik start validating port type
    resetValues.portType = '';
    resetValues.minTemperature = getDefaultMinTemperatureValue().toString();
    resetValues.maxTemperature = getDefaultMaxTemperatureValue().toString();
    setValues(resetValues);
  };

  const updateCommonVolumetricFlowRateUnit = (selectedUnitName: string, updateMaxAirflow = true) => {
    const updatedUnit = UnitService.getEnumForUnitName(selectedUnitName, VolumetricFlowRateUnits);
    const sizingProgressUpdated = { ...sizingProgress };
    if (sizingProgressUpdated.savedSizingRequest) {
      const unitBefore = filteredAirflowUnits.find(
        (unitDefinition) => unitDefinition.unitName === sizingProgressUpdated.savedSizingRequest?.maxAirflowUnit,
      )?.unit;

      const unitCurrent = filteredAirflowUnits.find((unitDefinition) => unitDefinition.unitName === selectedUnitName)?.unit;

      if (sizingProgressUpdated.savedSizingRequest.maxAirflow && updateMaxAirflow) {
        sizingProgressUpdated.savedSizingRequest.maxAirflow = convertUnitTo(
          UnitGroup.VolumetricFlowRate,
          unitBefore as number,
          sizingProgressUpdated.savedSizingRequest.maxAirflow,
          unitCurrent as number,
        );
      }
      sizingProgressUpdated.savedSizingRequest.maxAirflowUnit = updatedUnit;
    }
    setSizingProgress(sizingProgressUpdated);
  };

  const onTabClosing = (e: Event) => {
    const customEvent = e as CustomEvent;
    if (!customEvent || !customEvent.detail?.workflowId) return;
    if (customEvent.detail.workflowId === props.part.workflowId) {
      const cleanupEvent = new CustomEvent(compareCleanupEventType, {
        cancelable: false,
        detail: { workflowId: props.part.workflowId },
      });
      window.dispatchEvent(cleanupEvent);
    }
  };

  const advanceManifoldToSizeStep = async () => {
    const part = props.part;
    const sizeStep = part.workflowSteps?.find((step) => step.name === 'Size');
    const isAtSizeStep = sizeStep?.isCompleted;
    if (isAtSizeStep) return;

    await SystemService.updatePartWorkflow(
      part.workflowId as string,
      {
        id: part.workflowId,
        stepId: sizeStep?.id,
      },
      part.id as string,
    );
  };

  const updateCalculatedMetadataForValves = async () => {
    const valvesWithError: string[] = [];
    const calcResultSuccess: boolean[] = await SizingToolService.updateCalculatedMetadataForParts(
      props.segmentGroup.parts.map((part) => part.instance.id as string),
    );
    props.segmentGroup.parts.map((valve, i) => {
      if (!calcResultSuccess[i]) {
        valvesWithError.push(valve.instance.name as string);
      }
    });
    if (valvesWithError.length) toast.error(t('ErrorWhileCalculatingValues', { partName: valvesWithError.toString() }));
  };

  useEffect(() => {
    validateForm(true);
    window.addEventListener(tabClosingEventType, onTabClosing);
    updateCalculatedMetadataForValves()
      .then(() => updateProgressDetailResponse())
      .then(() => advanceManifoldToSizeStep());
    return () => {
      window.removeEventListener(tabClosingEventType, onTabClosing);
    };
  }, []);

  useEffect(() => {
    setValveSequencingApplications(getApplicationsArray());
  }, [sizingProgress?.savedSizingRequest?.maxAirflowUnit]);

  useEffect(() => {
    //handle valveInputs/virtualGroups mismatch
    const sizingProgressUpdated = {
      ...sizingProgress,
    };
    if (sizingProgressUpdated.savedSizingRequest) {
      //update newly added valves
      const newValveInputs: SizeValveOnManifoldRequest[] = [];
      props.system.virtualGroups?.map((virtualGroup) => {
        const currentApplication = sizingProgressUpdated.savedSizingRequest?.valveInputs?.find(
          (valveInput) => valveInput.partId === virtualGroup.intersectedPartId,
        );
        if (!currentApplication) {
          newValveInputs.push({
            partId: virtualGroup.intersectedPartId,
            valveFunction: undefined,
            operatorType: undefined,
            useRegulator: false,
          });
        }
      });
      sizingProgressUpdated.savedSizingRequest.valveInputs = [
        ...(sizingProgressUpdated.savedSizingRequest.valveInputs as SizeValveOnManifoldRequest[]),
        ...newValveInputs,
      ];
      //filter deleted valves
      const filteredValveInputs = sizingProgressUpdated.savedSizingRequest?.valveInputs?.filter((valveInput) =>
        props.system.virtualGroups?.some((virtualGroup) => valveInput.partId === virtualGroup.intersectedPartId),
      );
      sizingProgressUpdated.savedSizingRequest.valveInputs = filteredValveInputs;
      setSizingProgress(sizingProgressUpdated);
    }
  }, [props.system, sizingProgress.productCacheStatus]);

  useEffect(() => {
    validateForm();
  }, [sizingProgress]);

  const clearConnectionTypeSelection = () => {
    setFieldValue('electricalConnectionType', '');
    const sizingProgressUpdated = { ...sizingProgress };
    if (sizingProgressUpdated.savedSizingRequest) sizingProgressUpdated.savedSizingRequest.electricalConnectionType = undefined;
    setSizingProgress(sizingProgressUpdated);
  };

  const clearConnectionCategoryAndTypeSelections = () => {
    setFieldValue('electricalConnectionCategory', '');
    const sizingProgressUpdated = { ...sizingProgress };
    if (sizingProgressUpdated.savedSizingRequest) {
      sizingProgressUpdated.savedSizingRequest.electricalConnectionCategory = undefined;
    }
    setSizingProgress(sizingProgressUpdated);
    clearConnectionTypeSelection();
  };

  useEffect(() => {
    if (onlyEndPlateCollectiveWireIsValid) {
      const endPlate = manifoldElectricalConnectionCategoryOptionsMap.get(`${t('EndPlateCollectiveWire')}`);
      setFieldValue('electricalConnectionCategory', endPlate);
      const sizingProgressUpdated = {
        ...sizingProgress,
      };
      if (sizingProgressUpdated.savedSizingRequest) {
        sizingProgressUpdated.savedSizingRequest.electricalConnectionCategory = endPlate;
        setSizingProgress(sizingProgressUpdated);
      }
    }
  }, [sizingProgress.savedSizingRequest?.valveOperatorVoltage, sizingProgress.savedSizingRequest?.electricalConnectionToManifold]);

  useEffect(() => {
    if (sizingProgress.savedSizingRequest?.valveOperatorVoltage === valveOperatorVoltageOptionsMap.get(`${t('240VAC')}`)) {
      clearConnectionCategoryAndTypeSelections();
      const sizingProgressUpdated = {
        ...sizingProgress,
      };
      if (sizingProgressUpdated.savedSizingRequest) {
        sizingProgressUpdated.savedSizingRequest.electricalConnectionToManifold = valveElectricalConnectionOptionsMap.get(`${t('Non-Plug-in')}`);
        setSizingProgress(sizingProgressUpdated);
      }
    }
  }, [sizingProgress.savedSizingRequest?.valveOperatorVoltage]);

  useEffect(() => {
    const defaultAirflowUnitName = filteredAirflowUnits.find((unit) => (isMetric ? unit.isDefaultForMetric : unit.isDefaultForImperial))?.unitName;
    updateCommonVolumetricFlowRateUnit(defaultAirflowUnitName as string);

    const sizingProgressUpdated = {
      ...sizingProgress,
    };
    if (sizingProgressUpdated.savedSizingRequest) {
      sizingProgressUpdated.savedSizingRequest.minTemperatureUnit = defaultTemperatureUnit;
      sizingProgressUpdated.savedSizingRequest.maxTemperatureUnit = defaultTemperatureUnit;
    }
    setSizingProgress(sizingProgressUpdated);
  }, [isMetric]);

  const getSizingRequest = () => {
    updateSizingInputsFromValues(values);
    const sizingRequest = {
      partCategoryId: manifoldPartCategory?.id,
      partCategoryName: manifoldPartCategory?.name,
      workflowId: props.part.workflowId,
      locale: props.system.locale,
      divisionId: divisionId,
      skipMicroFilter: false,
      inputs: sizingProgress?.savedSizingRequest,
      overlapGroupLinkIds: props.system.overlapGroupLinks?.map((link) => link.id) as number[],
      manifoldGroupId: props.manifoldGroup.instance.id,
      startRow: props.part.row,
    };
    return sizingRequest;
  };

  const handleDownloadSizingDebuggingInfo = () => {
    const sizingRequest = getSizingRequest();
    const overlapGroupLinks = props.system.overlapGroupLinks?.filter((link) => link.intersectedGroupId === props.segmentGroup.instance.id);
    const previousParts = overlapGroupLinks?.map((link) =>
      props.system.parts
        ?.filter((part) => part.id === link.linkedPartId)
        .map((part) => {
          return {
            id: part.id,
            name: part.name,
            metadata: part.metadataInstances,
          };
        }),
    );
    const systemName = props.system.name as string;
    const debugInfo = {
      previousParts: previousParts,
      sizingRequest: sizingRequest,
      systemName: systemName,
      timestamp: new Date().toISOString(),
    };
    const debugString = JSON.stringify(debugInfo);
    const fileName = `VE system ${systemName}`;

    const inputDebugInfo = document.getElementById('sizingDebugInfo') as HTMLInputElement;
    const inputFileName = document.getElementById('sizingDebugFileName') as HTMLInputElement;
    const form = document.getElementById('downloadSizingDebugInfo') as HTMLFormElement;
    if (!inputDebugInfo || !inputFileName || !form) return;
    inputDebugInfo.value = debugString;
    inputFileName.value = fileName;
    form.submit();
  };

  const handleGenerateResults = async (loggingEnabled: boolean, seriesNameFilter: string) => {
    const sizingRequest = getSizingRequest() as SizeManifoldAndValvesRequest;
    sizingRequest.loggingEnabled = loggingEnabled;
    sizingRequest.filterProductOverride = seriesNameFilter;
    const response = await SizingToolService.createSizingManifoldAndValves(sizingRequest);
    return response !== undefined;
  };

  const handleReceivedResults = (productSeriesResults: ProductSeriesFilterResult[], compareViewModel: ComparisonViewModel) => {
    setPreviousSeriesResults(productSeriesResults);
    setComparisonViewModel({
      allProducts: compareViewModel,
      selectedProducts: compareViewModel,
    });
  };

  const handleProductSeriesResultsToCompare = (productsToCompare: ProductSeriesFilterResult[]) => {
    setPreviousProductsToCompare(productsToCompare);
  };

  const clearProductSeriesResults = () => {
    setPreviousSeriesResults([]);
  };

  const renderTooltip = (content: string) => {
    return (
      <LightTooltip title={content} arrow placement="left" className={styles['tooltip-content-div']}>
        <span className={styles['question-sign-icon']}>
          <img src="images/question_sign.png" />
        </span>
      </LightTooltip>
    );
  };

  const getValveOperatorTypeOptionInfoArray = (valveInput: SizeValveOnManifoldRequest): OptionInfo[] => {
    const functionIsTwoPosition = ['3/2', '4/2', '5/2'].some((valveFunction) => valveInput.valveFunction === valveFunction);
    const functionIsThreePosition = ['5/3 CE', '5/3 PC', '5/3 APB', '4/3 CE', '4/3 PC', '4/3 APB'].some(
      (valveFunction) => valveInput.valveFunction === valveFunction,
    );
    const voltageIsRemote = sizingProgress.savedSizingRequest?.valveOperatorVoltage === valveOperatorVoltageOptionsMap.get(`${t('RemotePilot')}`);
    const threePositionAndRemote = functionIsThreePosition && voltageIsRemote;
    const threePositionAndNotRemote = functionIsThreePosition && !voltageIsRemote;

    return Array.from(valveOperatorTypeOptionsMap.keys()).map((optionKey) => {
      const optionIsDoubleRemote = optionKey === t('DoubleRemotePilot');
      const optionIsDoubleSolenoid = optionKey === t('DoubleSolenoid');
      const optionIsRemote = optionKey === t('SingleRemotePilot') || optionIsDoubleRemote;

      const allButRemotes = functionIsTwoPosition && voltageIsRemote && !optionIsRemote;
      const allButDoubleRemote = threePositionAndRemote && !optionIsDoubleRemote;
      const allRemotes = !voltageIsRemote && optionIsRemote;
      const allButDoubleSolenoid = threePositionAndNotRemote && !optionIsDoubleSolenoid;

      const selectDoubleRemote = threePositionAndRemote && optionIsDoubleRemote;
      const selectDoubleSolenoid = threePositionAndNotRemote && optionIsDoubleSolenoid;

      return {
        displayText: optionKey,
        value: valveOperatorTypeOptionsMap.get(optionKey),
        disabled: allButRemotes || allButDoubleRemote || allRemotes || allButDoubleSolenoid,
        selected: selectDoubleRemote || selectDoubleSolenoid,
      } as OptionInfo;
    });
  };

  const getValveOperatorTypeValue = (valveInput: SizeValveOnManifoldRequest): string | null | undefined => {
    let valveOperatorType = valveInput.operatorType;
    const valveOperatorTypeOptions = getValveOperatorTypeOptionInfoArray(valveInput);
    const currentValveOperatorTypeOptionIsDisabled = valveOperatorTypeOptions.find((option) => option.value === valveInput.operatorType)?.disabled;
    if (currentValveOperatorTypeOptionIsDisabled) {
      valveOperatorType = undefined;
    }
    const valveOperatorTypeValueToSelect = valveOperatorTypeOptions.find((option) => option.selected)?.value;
    if (valveOperatorTypeValueToSelect) {
      valveOperatorType = valveOperatorTypeValueToSelect;
    }
    return valveOperatorType;
  };

  const renderValveOperatorMenuItems = (valveInput: SizeValveOnManifoldRequest) => {
    const options = getValveOperatorTypeOptionInfoArray(valveInput);
    return options.map((option) => {
      return (
        <MenuItem disabled={option.disabled} key={option.value} value={option.value}>
          {option.displayText}
        </MenuItem>
      );
    });
  };

  const renderCompareTool = () => {
    return (
      <CompareTool
        workflowId={props.part.workflowId as string}
        partCategoryName={manifoldPartCategory?.name as string}
        closeCompare={() => closeCompare()}
        locale={SystemService.getCurrentState().system?.locale as string}
        viewModel={comparisonViewModel.selectedProducts}
        productAttributeKeyForGrouping="Valve Series"
        part={props.part}
      />
    );
  };

  const getOverview = () => {
    const guideFile = guideMap.get(ApiService.getState().language as string);
    return (
      <Trans
        i18nKey="ManifoldOverview"
        components={{
          p: <p />,
          div: <div />,
          ol: <ol />,
          li: <li />,
          helpFileLink: <a href={`${process.env.REACT_APP_LEGACY_BASE_HOST}/Content/help/${guideFile}`} target="_blank" rel="noreferrer" />,
        }}
      />
    );
  };

  const renderSizingTool = () => {
    return (
      <>
        <div className={styles['sizing-inputs-container']}>
          <SizingToolHeader toolName={t('Manifold')} isMetric={isMetric} setIsMetric={setIsMetric} guideMap={guideMap} overview={getOverview()} />
          <AirFlowCalculator
            divisionId={divisionId}
            showAirFlowCalculator={showAirFlowCalculator}
            closeAirFlowCalculatorModal={() => setShowAirFlowCalculator(false)}
            system={props.system}
            getApplicationsArray={getApplicationsArray}
            commonVolumetricFlowRateUnitName={sizingProgress?.savedSizingRequest?.maxAirflowUnit}
            sizingProgress={sizingProgress}
            setSizingProgress={setSizingProgress}
            filteredAirflowUnits={filteredAirflowUnits as UnitDefinition[]}
            getAirflowInCommonUnit={getAirflowInCommonUnit}
            getValveCv={getValveCv}
            validateStationsAndMaxAirflow={validateStationsAndMaxAirflow}
            segmentGroup={props.segmentGroup}
          />
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<FontAwesomeIcon className={styles['chevron-icon']} icon="chevron-down" />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              className={styles['accordion-header']}
            >
              <Typography>{t('ManifoldInputs')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div className={styles['input-div-1']} id={'valveSequenceDiv'}>
                {renderTooltip(`${t('ValveSequencingTooltip')}`)}
                <p>{t('ValveSequencing')}</p>
                <div className={styles['max-airflow-input-container']}>
                  <label htmlFor="maxAirFlow" className={styles['required-label']}>
                    {t('MaxAirflow')}
                  </label>
                  <div onClick={() => setShowAirFlowCalculator(true)}>
                    <TextField
                      placeholder={t('MaxAirflowPlaceholder')}
                      id="maxAirFlow"
                      InputProps={{
                        inputProps: {
                          style: { textAlign: 'right', opacity: '0.5' },
                          readOnly: true,
                        },
                      }}
                      value={
                        sizingProgress?.savedSizingRequest?.maxAirflow
                          ? toDecimals(sizingProgress.savedSizingRequest.maxAirflow, props.system.divisionId as number)
                          : ''
                      }
                      className={styles['max-airflow-input-field']}
                    />
                  </div>
                  <UnitPicker
                    group={UnitGroup?.VolumetricFlowRate}
                    subGroup={2}
                    value={sizingProgress?.savedSizingRequest?.maxAirflowUnit?.toString()}
                    changeHandler={updateCommonVolumetricFlowRateUnit}
                    unitDefinitions={filteredAirflowUnits}
                  />
                  <div
                    className={styles['calculate-button']}
                    onClick={() => {
                      setShowAirFlowCalculator(true);
                    }}
                  >
                    {t('AirflowCalculator')}
                  </div>
                </div>
                {isMaxAirflowMissing && <span className={styles['error-text']}>{t('ThisFieldIsRequired')}</span>}
                {invalidMaxAirflow && <span className={styles['error-text']}>{t('ValueMustBePositive')}</span>}
              </div>
              <div className={styles['input-div-2']}>
                {renderTooltip(`${t('ValveOperatorVoltageTooltip')}`)}
                <p className={styles['required-label']}>{t('ValveOperatorVoltage')}</p>
                <div className={styles['options-div']}>
                  {Array.from(valveOperatorVoltageOptionsMap.keys()).map((option: string) => {
                    const optionValue = valveOperatorVoltageOptionsMap.get(option);
                    return (
                      <div key={optionValue}>
                        <input
                          name="valveOperatorVoltage"
                          type="radio"
                          id={option}
                          checked={sizingProgress?.savedSizingRequest?.valveOperatorVoltage === optionValue}
                          onChange={(e) => {
                            if (valveOperatorVoltageOptionsMap.get(`${t('RemotePilot')}`) === optionValue) {
                              clearConnectionCategoryAndTypeSelections();
                            }
                            handleChange(e);
                            const sizingProgressUpdated = {
                              ...sizingProgress,
                            };
                            if (sizingProgressUpdated.savedSizingRequest) {
                              sizingProgressUpdated.savedSizingRequest.valveOperatorVoltage = optionValue;

                              //update electical connection to manifold
                              if (optionValue === 'N/A') {
                                sizingProgressUpdated.savedSizingRequest.electricalConnectionToManifold = valveElectricalConnectionOptionsMap.get(
                                  `${t('Non-Plug-in')}`,
                                );
                              }
                              sizingProgressUpdated.savedSizingRequest.valveInputs?.forEach(
                                (valveInput) => (valveInput.operatorType = getValveOperatorTypeValue(valveInput)),
                              );
                            }
                            setSizingProgress(sizingProgressUpdated);
                          }}
                        ></input>
                        <label htmlFor={option}>{option}</label>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className={styles['input-div-3']} id="connectionToManifoldDiv">
                {renderTooltip(`${t('ValveElectricalConnectionToManifoldTooltip')}`)}
                <p className={styles['required-label']}>{t('ValveElectricalConnectionToManifold')}</p>
                <div className={styles['options-div']}>
                  {Array.from(valveElectricalConnectionOptionsMap.keys()).map((option: string) => {
                    const optionValue = valveElectricalConnectionOptionsMap.get(option);
                    const disablePlugIn =
                      valveElectricalConnectionOptionsMap.get(`${t('Plug-in')}`) === optionValue &&
                      (sizingProgress.savedSizingRequest?.valveOperatorVoltage === valveOperatorVoltageOptionsMap.get(`${t('240VAC')}`) ||
                        sizingProgress.savedSizingRequest?.valveOperatorVoltage === valveOperatorVoltageOptionsMap.get(`${t('RemotePilot')}`));
                    return (
                      <div key={optionValue}>
                        <input
                          name="electricalConnectionToManifold"
                          id={option}
                          disabled={disablePlugIn}
                          type="radio"
                          onChange={(e) => {
                            clearConnectionCategoryAndTypeSelections();
                            handleChange(e);
                            const sizingProgressUpdated = {
                              ...sizingProgress,
                            };
                            if (sizingProgressUpdated.savedSizingRequest) sizingProgressUpdated.savedSizingRequest.electricalConnectionToManifold = optionValue;
                            setSizingProgress(sizingProgressUpdated);
                          }}
                          checked={sizingProgress?.savedSizingRequest?.electricalConnectionToManifold === optionValue}
                        ></input>
                        <label htmlFor={option} className={styles[disablePlugIn ? 'disabled-label' : '']}>
                          {option}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className={styles['input-div-4']} id="connectionCategoryDiv">
                {renderTooltip(`${t('ManifoldElectricalConnectionCategoryTooltip')}`)}
                <p className={styles['required-label']}>{t('ManifoldElectricalConnectionCategory')}</p>
                <div className={styles['options-div']}>
                  {Array.from(manifoldElectricalConnectionCategoryOptionsMap.keys()).map((option: string) => {
                    const optionValue = manifoldElectricalConnectionCategoryOptionsMap.get(option);
                    const allButIndustrialNetworkFieldBus =
                      onlyEndPlateCollectiveWireIsValid &&
                      manifoldElectricalConnectionCategoryOptionsMap.get(`${t('Industrial-Network-Fieldbus')}`) === optionValue;
                    return (
                      <div key={optionValue}>
                        <input
                          name="electricalConnectionCategory"
                          id={option}
                          disabled={isNonPlugInSelected || allButIndustrialNetworkFieldBus}
                          type="radio"
                          onChange={(e) => {
                            clearConnectionTypeSelection();
                            handleChange(e);
                            const sizingProgressUpdated = {
                              ...sizingProgress,
                            };
                            if (sizingProgressUpdated.savedSizingRequest) {
                              sizingProgressUpdated.savedSizingRequest.electricalConnectionCategory = optionValue;
                            }
                            setSizingProgress(sizingProgressUpdated);
                          }}
                          checked={sizingProgress?.savedSizingRequest?.electricalConnectionCategory === optionValue}
                        ></input>
                        <label htmlFor={option} className={styles[isNonPlugInSelected || allButIndustrialNetworkFieldBus ? 'disabled-label' : '']}>
                          {option}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {errors.electricalConnectionCategory && touched.electricalConnectionCategory && (
                  <span className={styles['error-text']}>{errors.electricalConnectionCategory}</span>
                )}
              </div>
              <div className={styles['input-div-5']} id={'connectionTypeDiv'}>
                {renderTooltip(`${t('ManifoldElectricalConnectionTypeTooltip')}`)}
                <p className={styles['required-label']}>{t('ManifoldElectricalConnectionType')}</p>
                <div className={styles['options-div']}>
                  {Array.from(manifoldElectricalConnectionTypeOptionsMap.keys()).map((option: string) => {
                    const optionValue = manifoldElectricalConnectionTypeOptionsMap.get(option);
                    const electricalConnectionCategory = sizingProgress.savedSizingRequest?.electricalConnectionCategory;
                    const isDisabled =
                      isNonPlugInSelected ||
                      (electricalConnectionCategory === `${t('Industrial-Network-Fieldbus')}` && industrialFieldbusTypes.every((type) => type !== option)) ||
                      (electricalConnectionCategory === `${t('EndPlateCollectiveWire')}` && endPlateCollectiveWireTypes.every((type) => type !== option));
                    return (
                      <div key={optionValue}>
                        <input
                          name="electricalConnectionType"
                          id={option}
                          disabled={isDisabled}
                          type="radio"
                          onChange={(e) => {
                            handleChange(e);
                            const sizingProgressUpdated = {
                              ...sizingProgress,
                            };
                            if (sizingProgressUpdated.savedSizingRequest) sizingProgressUpdated.savedSizingRequest.electricalConnectionType = optionValue;
                            setSizingProgress(sizingProgressUpdated);
                          }}
                          checked={sizingProgress?.savedSizingRequest?.electricalConnectionType === optionValue}
                        ></input>
                        <label htmlFor={option} className={styles[isDisabled ? 'disabled-label' : '']}>
                          {option}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {errors.electricalConnectionType && touched.electricalConnectionType && (
                  <span className={styles['error-text']}>{errors.electricalConnectionType}</span>
                )}
                {!errors.electricalConnectionType && touched.electricalConnectionType && !isElectricalConnectionTypeValid() && (
                  <span className={styles['error-text']}>{thisFieldIsRequiredMessage}</span>
                )}
              </div>
              <div className={styles['input-div-6']} id={'portTypeDiv'}>
                {renderTooltip(`${t('PortTypeTooltip')}`)}
                <p className={styles['required-label']}>{t('PortType')}</p>
                <div className={styles['options-div']}>
                  {Array.from(portTypeOptionsMap.keys()).map((option: string) => {
                    const optionValue = portTypeOptionsMap.get(option) as string;

                    return (
                      <div key={optionValue}>
                        <input
                          name="portType"
                          type="radio"
                          id={option}
                          onChange={(e) => {
                            handleChange(e);
                            const sizingProgressUpdated = {
                              ...sizingProgress,
                            };
                            if (sizingProgressUpdated.savedSizingRequest) sizingProgressUpdated.savedSizingRequest.portType = optionValue;
                            setSizingProgress(sizingProgressUpdated);
                          }}
                          checked={sizingProgress?.savedSizingRequest?.portType === optionValue}
                        ></input>
                        <label htmlFor={option}>{option}</label>
                      </div>
                    );
                  })}
                </div>
                {errors.portType && touched.portType && <span className={styles['error-text']}>{errors.portType}</span>}
              </div>
              <div className={styles['input-div-7']} id={'valveTemperatureDiv'}>
                <p>{t('ValveTemperature')}</p>
                <div className={styles['options-div']}>
                  <div className={styles['minimum-temperature-container']}>
                    {renderTooltip(`${t('MinValveTemperatureTooltip')}`)}
                    <label htmlFor="minTemperature" className={styles['required-label']}>
                      {t('ValveMinimumTemperature')}
                    </label>
                    <div className={styles['temperature-input-field-error-wrapper']}>
                      <div className={styles['minimum-temperature-input-fields']}>
                        <div>
                          <TextField
                            id="minTemperature"
                            onChange={(e) => {
                              handleChange(e);
                              const sizingProgressUpdated = { ...sizingProgress };
                              const inputs = sizingProgressUpdated.savedSizingRequest as SizeManifoldAndValvesInputs;
                              inputs.minTemperature = convertDecimalStringToNumber(e.target.value, props.system.locale);
                              setSizingProgress(sizingProgressUpdated);
                            }}
                            onBlur={(e) => {
                              handleBlur(e);
                            }}
                            value={values.minTemperature}
                            InputProps={{
                              inputProps: {
                                style: { textAlign: 'right' },
                              },
                            }}
                            className={styles['temperature-input-field']}
                          />
                        </div>
                        <UnitPicker
                          group={UnitGroup?.Temperature}
                          subGroup={1}
                          value={sizingProgress?.savedSizingRequest?.minTemperatureUnit?.toString()}
                          changeHandler={(unitName: string) => {
                            const unit = UnitService.getEnumForUnitName(unitName, TemperatureUnits);
                            const sizingProgressUpdated = { ...sizingProgress };
                            if (sizingProgressUpdated.savedSizingRequest) sizingProgressUpdated.savedSizingRequest.minTemperatureUnit = unit;
                            setSizingProgress(sizingProgressUpdated);
                          }}
                        />
                      </div>
                      {errors.minTemperature && touched.minTemperature && <div className={styles['error-text']}>{errors.minTemperature}</div>}
                    </div>
                  </div>
                  <div className={styles['maximum-temperature-container']}>
                    {renderTooltip(`${t('MaxValveTemperatureTooltip')}`)}
                    <label htmlFor="maxTemperature" className={styles['required-label']}>
                      {t('ValveMaximumTemperature')}
                    </label>
                    <div className={styles['temperature-input-field-error-wrapper']}>
                      <div className={styles['maximum-temperature-input-fields']}>
                        <div>
                          <TextField
                            id="maxTemperature"
                            onChange={(e) => {
                              handleChange(e);
                              const sizingProgressUpdated = { ...sizingProgress };
                              const inputs = sizingProgressUpdated.savedSizingRequest as SizeManifoldAndValvesInputs;
                              inputs.maxTemperature = convertDecimalStringToNumber(e.target.value, props.system.locale);
                              setSizingProgress(sizingProgressUpdated);
                            }}
                            onBlur={(e) => {
                              handleBlur(e);
                            }}
                            InputProps={{
                              inputProps: {
                                style: { textAlign: 'right' },
                              },
                            }}
                            value={values.maxTemperature}
                            className={styles['temperature-input-field']}
                          />
                        </div>
                        <UnitPicker
                          group={UnitGroup?.Temperature}
                          subGroup={1}
                          value={sizingProgress?.savedSizingRequest?.maxTemperatureUnit?.toString()}
                          changeHandler={(unitName: string) => {
                            const unit = UnitService.getEnumForUnitName(unitName, TemperatureUnits);
                            const sizingProgressUpdated = { ...sizingProgress };
                            if (sizingProgressUpdated.savedSizingRequest) sizingProgressUpdated.savedSizingRequest.maxTemperatureUnit = unit;
                            setSizingProgress(sizingProgressUpdated);
                          }}
                        />
                      </div>
                      {errors.maxTemperature && touched.maxTemperature && <div className={styles['error-text']}>{errors.maxTemperature}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>

          {sizingProgress?.savedSizingRequest?.valveInputs?.map((valveInput, index) => {
            const applicationName = valveSequencingApplications[index] ? valveSequencingApplications[index].name : '';
            const airflowValue = valveSequencingApplications[index] ? getAirflowInCommonUnit(valveSequencingApplications[index].id as number) : NaN;
            const airflow = toDecimals(airflowValue, props.system.divisionId as number) || '-';
            const airflowUnitLabel = UnitService.getLabelForUnitName(sizingProgress.savedSizingRequest?.maxAirflowUnit?.toString(), filteredAirflowUnits);
            return (
              <div
                key={valveInput.partId}
                className={
                  (!valveInput.valveFunction && isAnyValveOptionMissing) || (!valveInput.operatorType && isAnyValveOptionMissing) ? 'valveOptionsDiv' : ''
                }
              >
                <Accordion defaultExpanded style={{ minHeight: 'none' }}>
                  <AccordionSummary
                    expandIcon={<FontAwesomeIcon className={styles['chevron-icon']} icon="chevron-down" />}
                    aria-controls="panel2a-content"
                    id="panel2a-header"
                    className={styles['accordion-header']}
                  >
                    <Typography>
                      <span>{t('StationNumberValveOptions', { number: index + 1 })}</span>
                    </Typography>

                    <Typography className={styles['accordion-end-text']}>
                      <span>{applicationName}</span>
                      <span>{airflow}</span>
                      <span>{airflowUnitLabel}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className={styles['options-div']}>
                      <div className={styles['valve-options-div-tooltip']}>{renderTooltip(`${t('ValveFunctionRequirementsTooltip')}`)}</div>
                      <div className={styles['valve-function-requirements-container']}>
                        <label htmlFor={'valveFunctionStation' + (index + 1).toString()} className={styles['required-label']}>
                          {t('ValveFunctionRequirements')}
                        </label>

                        <div className={styles['valve-function-requirements-input-field']}>
                          <FormControl className={styles['valve-function-requirements-dropdown']} size="small">
                            <Select
                              inputProps={{ id: 'valveFunctionStation' + (index + 1).toString() }}
                              color="secondary"
                              displayEmpty
                              value={valveInput.valveFunction ?? ''}
                              onChange={(e) => {
                                if (index === 0 && !('valveOptions' in touched)) {
                                  setFieldTouched('valveOptions', true);
                                }
                                const sizingProgressUpdated = { ...sizingProgress };
                                if (sizingProgressUpdated?.savedSizingRequest?.valveInputs) {
                                  sizingProgressUpdated.savedSizingRequest.valveInputs[index].valveFunction = e.target.value;

                                  const updatedValveInput = sizingProgressUpdated.savedSizingRequest.valveInputs[index];
                                  sizingProgressUpdated.savedSizingRequest.valveInputs[index].operatorType = getValveOperatorTypeValue(updatedValveInput);
                                  setSizingProgress(sizingProgressUpdated);
                                }
                              }}
                            >
                              {valveFunctionOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {t(option)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!valveInput.valveFunction && isAnyValveOptionMissing && <span className={styles['error-text']}>{t('ThisFieldIsRequired')}</span>}
                        </div>
                      </div>

                      <div className={styles['valve-operator-type-container']}>
                        <label htmlFor={'valveOperatorType' + (index + 1).toString()} className={styles['required-label']}>
                          {t('ValveOperatorType')}
                        </label>

                        <div className={styles['valve-operator-type-input-field']}>
                          <FormControl className={styles['valve-operator-type-dropdown']} size="small">
                            <Select
                              inputProps={{ id: 'valveOperatorType' + (index + 1).toString() }}
                              color="secondary"
                              displayEmpty
                              value={valveInput.operatorType ?? ''}
                              onChange={(e) => {
                                const sizingProgressUpdated = { ...sizingProgress };
                                if (sizingProgressUpdated?.savedSizingRequest?.valveInputs)
                                  sizingProgressUpdated.savedSizingRequest.valveInputs[index].operatorType = e.target.value;
                                setSizingProgress(sizingProgressUpdated);
                              }}
                            >
                              {renderValveOperatorMenuItems(valveInput)}
                            </Select>
                          </FormControl>
                          {!valveInput.operatorType && isAnyValveOptionMissing && <span className={styles['error-text']}>{t('ThisFieldIsRequired')}</span>}
                        </div>
                      </div>

                      <div className={styles['use-regulators-container']}>
                        <label htmlFor={'useRegulator' + (index + 1).toString()}>{t('UseRegulatorsIfNeeded')}</label>
                        <div className={styles['use-regulators-toggle-switch']}>
                          <div className={styles['switch-container']}>
                            <span>{t('No')}</span>
                            <Switch
                              inputProps={{ id: 'useRegulator' + (index + 1).toString() }}
                              className="toggle-switch-sizing"
                              checked={valveInput.useRegulator}
                              onChange={() => {
                                const sizingProgressUpdated = { ...sizingProgress };
                                if (sizingProgressUpdated?.savedSizingRequest?.valveInputs)
                                  sizingProgressUpdated.savedSizingRequest.valveInputs[index].useRegulator = !valveInput.useRegulator;
                                setSizingProgress(sizingProgressUpdated);
                              }}
                            />
                            <span>{t('Yes')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          })}
          <div className={styles['back-to-top']}>
            <Button onClick={() => document.getElementById('dynaTabStrip')?.scrollIntoView()} variant="contained">
              {t('BackToTop')}
            </Button>
          </div>
        </div>
        <ResultsPanel
          part={props.part}
          isMetric={isMetric}
          hasCompareContainer={!!comparisonViewModel.allProducts.products?.length}
          productCacheStatus={sizingProgress.productCacheStatus as ProductCacheStatus}
          selectAllProductsToCompare={true}
          seriesNameFilterExample={'P2MA or P2LAX | * | N or HU20 | M4 | 0 | 0 | 36 | 49 | *)'}
          previousProductSeriesResults={previousSeriesResults}
          previousProductSeriesResultsToCompare={previousProductsToCompare}
          handleProductSeriesResultsToCompare={handleProductSeriesResultsToCompare}
          handleSaveProgress={handleSubmitToSaveProgress}
          handleCompare={handleCompare}
          resetSizingRequest={resetSizingRequest}
          validateForm={validateForm}
          scrollErrorIntoView={scrollErrorIntoView}
          handleGenerateResults={handleGenerateResults}
          handleReceivedResults={handleReceivedResults}
          clearProductSeriesResults={clearProductSeriesResults}
          renderInvalidFieldsErrorToast={renderInvalidFieldsErrorToast}
          handleDownloadSizingDebuggingInfo={handleDownloadSizingDebuggingInfo}
        />
      </>
    );
  };

  return (
    <div id="sizingToolContainer" className={styles['sizing-tool-container']}>
      {showCompare == false && renderSizingTool()}
      {showCompare == true && renderCompareTool()}
    </div>
  );
}
